import { MessageSquare, User } from "lucide-react";
import { contactLabel, formatPhone } from "../analysis/analysisLabels";

/**
 * O servidor devolve o trecho com o termo entre « », vindo do ts_headline.
 * Converter aqui evita ter que reimplementar o destaque no navegador — e o
 * recorte já vem centrado no termo, não nos primeiros caracteres da mensagem.
 */
const Snippet = ({ text }) => {
  const parts = String(text || "").split(/«|»/);

  return (
    <span>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <mark
            key={index}
            className="rounded bg-yellow-200 px-0.5 text-gray-900 dark:bg-yellow-500/40 dark:text-yellow-100"
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </span>
  );
};

const Row = ({ children, onClick }) => (
  <button
    onClick={onClick}
    className="w-full border-b border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
  >
    {children}
  </button>
);

const SearchResults = ({ result, loading, onOpen }) => {
  if (loading && !result) {
    return (
      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
        Buscando...
      </div>
    );
  }

  if (!result) return null;

  if (result.tooShort) {
    return (
      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
        Digite ao menos 2 caracteres.
      </div>
    );
  }

  const empty = !result.conversations.length && !result.messages.length;

  if (empty) {
    return (
      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
        Nada encontrado para “{result.term}”.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {result.conversations.length > 0 && (
        <>
          <p className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            Contatos ({result.conversations.length})
          </p>

          {result.conversations.map((conversation) => {
            const name = contactLabel(conversation.contact);
            const phone = formatPhone(conversation.contact?.phoneNumber);

            return (
              <Row key={conversation.id} onClick={() => onOpen(conversation.id)}>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                    {name}
                  </span>
                  {conversation._count?.messages > 0 && (
                    <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-500 px-1.5 text-xs font-bold text-white">
                      {conversation._count.messages}
                    </span>
                  )}
                </div>
                {phone && phone !== name && (
                  <p className="mt-0.5 pl-6 text-xs text-gray-500 dark:text-gray-400">
                    {phone}
                  </p>
                )}
              </Row>
            );
          })}
        </>
      )}

      {result.messages.length > 0 && (
        <>
          <p className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-900 dark:text-gray-400">
            Mensagens ({result.messages.length}
            {result.hasMore ? "+" : ""})
          </p>

          {result.messages.map((message) => (
            <Row
              key={message.messageId}
              onClick={() => onOpen(message.conversationId)}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {contactLabel(message.conversation?.contact)}
                </span>
                <span className="ml-auto shrink-0 text-[11px] text-gray-400">
                  {new Date(message.sentAt).toLocaleDateString("pt-BR")}
                </span>
              </div>

              <p className="mt-1 pl-6 text-xs text-gray-600 dark:text-gray-300">
                <span className="text-gray-400">
                  {message.direction === "INCOMING" ? "Cliente: " : "Atendimento: "}
                </span>
                <Snippet text={message.snippet} />
              </p>
            </Row>
          ))}
        </>
      )}

      {result.tookMs !== undefined && (
        <p className="px-4 py-2 text-[11px] text-gray-400">
          Busca no servidor em {result.tookMs} ms
        </p>
      )}
    </div>
  );
};

export default SearchResults;
